import { reactive, computed, watch } from 'vue'
// Relative, not '@/': the unit tests load this file under Node.
import { gaitTimeSum, gaitsExceedDuration, wholeSeconds } from '../../lib/sessions/gait-total.js'

export const useGaitAllocation = (initialKeys, totalRef, emitFn) => {
    const values = reactive(
        initialKeys.reduce((acc, key) => {
            acc[key] = 0
            return acc
        }, {})
    )

    // Whole seconds (or whole meters), rounded as gait-total.js does.
    const totalSafe = computed(() => wholeSeconds(totalRef.value))
    const isLocked = computed(() => totalSafe.value === 0)

    const zeroAll = () => {
        initialKeys.forEach((key) => {
            values[key] = 0
        })
    }

    const remaining = computed(() => Math.max(0, totalSafe.value - gaitTimeSum(values, initialKeys)))

    const enforceLimits = () => {
        const total = totalSafe.value
        // Rounded like totalSafe: float values from the API (1200.4 + 600 on
        // 1800.4) fit the whole-second total and must not be zeroed.
        if (gaitsExceedDuration(values, total, initialKeys)) {
            zeroAll()
        } else {
            initialKeys.forEach((key) => {
                values[key] = Math.min(Math.max(values[key], 0), total)
            })
        }
    }

    const onSlider = (key, rawValue) => {
        values[key] = Math.min(Math.max(0, rawValue), totalSafe.value)
        enforceLimits()
        emitFn?.(values)
    }

    const nudge = (key, delta) => {
        const desired = values[key] + delta
        onSlider(key, desired)
    }

    watch(
        totalRef,
        () => {
            enforceLimits()
            emitFn?.(values)
        },
        { immediate: true }
    )

    return { values, totalSafe, isLocked, remaining, onSlider, nudge, enforceLimits, zeroAll }
}
